import { findIndex, sortBy } from 'lodash';
import React, { PropsWithChildren, ReactElement, ReactNode, useMemo } from 'react';
import { TruncatedTooltip } from 'src/ui/atoms/truncated-tooltip';
import Pagination, { PaginationProps } from 'ui/molecules/pagination';
import Table from 'ui/molecules/table';
import { HorizontalAlign } from 'ui/types/align';
import { DataRecordOrdering } from 'ui/types/data-ordering';
import * as Styled from './styled';
import PlaceholderFallback from 'src/ui/atoms/placeholder-fallback';
import Image from 'ui/atoms/image';
import Translate from 'src/ui/atoms/translate';
import usePlatformImage from 'src/ui/hooks/use-platform-image';
import Header from 'src/ui/atoms/header';
import CopyButton from 'src/ui/molecules/copy-button';
import Icon from 'src/ui/atoms/icon';
import Date from 'src/ui/atoms/date';
import { MultiSelectItem } from 'src/ui/molecules/filtering/filter-popup/filter-multi-select';
import { FilterPopup, FiltersWrapper } from 'src/ui/molecules/filtering';
import FilterGenerator from 'src/ui/molecules/filtering/filter-generator';

type DataType = {
  id?: string;
  [key: string]: any;
};

export enum CellTypeEnum {
  text = 'text',
  number = 'number',
  date = 'date',
  currency = 'currency',
  percentage = 'percentage',
}

export interface DataRecordTableCell<Data> {
  render: (data: Data) => ReactNode;
  orderingFieldName?: string;
  title?: ReactNode;
  filterName?: string;
  alignContent?: HorizontalAlign;
  width?: number;
  copyValue?: (data: Data) => ReactNode;
  disableCellTooltip?: boolean;
  type?: CellTypeEnum;
  getFilterData?: (params: any) => any;
  processFilterData?: (data: any[]) => any[];
  filterData?: MultiSelectItem[];
  searchFieldName?: string;
}

export interface DataRecordTableProps<Data extends DataType> {
  /** Primary content. */
  children?: ReactNode;
  title?: ReactNode;
  ordering?: DataRecordOrdering;
  onOrderBy?: (fieldName: string) => void;
  data?: Data[];
  cells?: Array<DataRecordTableCell<Data> | boolean>;
  hasPermission?: boolean;
  loading?: boolean;
  responsive?: boolean;
  paginationProps?: PaginationProps;
  onGoToDetails?: (id: string) => void;
  setFilterPopupHeight?: (height: number) => void;
  hidePaginationNavigation?: boolean;
  searchComponent?: ReactNode;
}

const DataRecordTable = <Data extends DataType>(
  props: PropsWithChildren<DataRecordTableProps<Data>>,
): ReactElement | null => {
  const {
    children,
    data,
    onOrderBy,
    ordering,
    title,
    cells,
    hasPermission = true,
    loading,
    responsive,
    paginationProps,
    onGoToDetails,
    setFilterPopupHeight,
    hidePaginationNavigation,
    searchComponent,
    ...restProps
  } = props;

  const renderedCells = useMemo(() => {
    const userCells = (cells?.filter(Boolean) || []) as Array<DataRecordTableCell<Data>>;

    const idCell: DataRecordTableCell<Data> = {
      title: <Translate name="common.id" />,
      render: ({ id }) => id,
      copyValue: ({ id }) => id && <CopyButton valueToCopy={id} />,
      orderingFieldName: 'id',
    };

    const createdAtCell: DataRecordTableCell<Data> = {
      title: <Translate name="common.created" />,
      render: (row) => {
        const dateValue = row.createdAt || row.registeredAt || row.sendAt || row.signedAt;
        return dateValue ? <Date showTime>{dateValue}</Date> : '-';
      },
      orderingFieldName: 'created_at',
      type: CellTypeEnum.date,
      filterName: 'createdAt',
    };

    const finalCells = [idCell, createdAtCell, ...userCells];

    if (onGoToDetails) {
      finalCells.push({
        title: '',
        render: (row) => (
          <Icon name="arrow-right" color="primary" onClick={() => row.id && onGoToDetails(row.id)} size="large" />
        ),
        width: 56,
      });
    }

    return finalCells;
  }, [cells, onGoToDetails]);

  const filterLimits = useMemo(() => {
    if (!paginationProps) return [];
    const sortedAvailableLimits = sortBy(paginationProps.availableLimits);
    const index = findIndex(sortedAvailableLimits, (limit) => paginationProps.count <= limit);
    return sortedAvailableLimits.slice(0, index === -1 ? sortedAvailableLimits.length : index + 1);
  }, [paginationProps?.availableLimits, paginationProps?.count]);

  const noDataImage = usePlatformImage('emptyView');
  const hasData = data && data.length > 0;

  const getFiltersAndSearchBar = () => {
    if (!hasPermission) return null;

    return (
      <FiltersWrapper>
        {!!searchComponent && searchComponent}
        <FilterPopup setFilterPopupHeight={setFilterPopupHeight}>
          {renderedCells.map((cell) => {
            if (cell.filterName) {
              return <FilterGenerator key={cell.filterName || cell.filterName} cellData={cell} />;
            }
            return null;
          })}
        </FilterPopup>
      </FiltersWrapper>
    );
  };

  if (loading && !hasData) {
    return getFiltersAndSearchBar();
  }

  return (
    <>
      {getFiltersAndSearchBar()}
      <Styled.DataRecordTable {...restProps}>
        <Table title={title}>
          <Table.Head>
            <Table.Row>
              {renderedCells.map(({ orderingFieldName, alignContent, title, width }, i) => {
                return (
                  <Table.HeadCell
                    key={i}
                    alignContent={alignContent}
                    onOrderBy={
                      orderingFieldName && onOrderBy && data && data.length > 1
                        ? () => orderingFieldName && onOrderBy(orderingFieldName)
                        : undefined
                    }
                    orderingDirection={ordering?.fieldName === orderingFieldName ? ordering?.direction : undefined}
                    width={width}
                  >
                    <TruncatedTooltip>{title}</TruncatedTooltip>
                  </Table.HeadCell>
                );
              })}
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {(!hasData || !hasPermission) && (
              <Table.Row>
                <Table.Cell colSpan={renderedCells.length}>
                  <Styled.NoDataContainer>
                    <Image src={noDataImage} size="small" />
                    <Header spacing="medium">
                      <Translate name="noTableData.title" />
                    </Header>
                    <Translate name="noTableData.description" />
                  </Styled.NoDataContainer>
                </Table.Cell>
              </Table.Row>
            )}

            {hasPermission &&
              data &&
              data.map((row, i) => (
                <Table.Row key={row.id || i} disabled={row.disabled}>
                  {renderedCells.map(({ alignContent, render, copyValue, disableCellTooltip }, i) => (
                    <Table.Cell key={i}>
                      <Styled.CellContainer $alignContent={alignContent}>
                        {disableCellTooltip && <PlaceholderFallback>{render(row)}</PlaceholderFallback>}
                        {!disableCellTooltip && (
                          <TruncatedTooltip>
                            <PlaceholderFallback>{render(row)}</PlaceholderFallback>
                          </TruncatedTooltip>
                        )}
                        {copyValue && copyValue(row)}
                      </Styled.CellContainer>
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))}
          </Table.Body>

          {hasPermission && data && data.length > 0 && paginationProps && (
            <Table.Foot>
              <Table.Row key={'pagination'}>
                <Table.Cell key={'pagination'} colSpan={renderedCells.length}>
                  <Pagination
                    {...paginationProps}
                    availableLimits={filterLimits}
                    hidePaginationNavigation={filterLimits.length <= 1 || hidePaginationNavigation}
                  />
                </Table.Cell>
              </Table.Row>
            </Table.Foot>
          )}
        </Table>
      </Styled.DataRecordTable>
    </>
  );
};

export default DataRecordTable;
